import {
  BaseAPI,
  ImpersonationSessionCreate,
  ImpersonationSessionWithToken,
  ImpersonationSessionWithTokenFromJSON,
  JSONApiResponse
} from "@wingspanhq/wingspan-api-sdk";
import { apiConfig } from "./index";

/**
 * The Account arm of the generated `ImpersonationSessionCreate`, which models
 * the target as `targetAccountId` XOR `targetPersonId`. Every minted token is
 * Account-bound and can never exercise Person-scoped endpoints — Person-target
 * impersonation is a draining legacy arm, not something this screen offers — so
 * the request type fixes the target kind instead of leaving it open.
 */
export type ImpersonationSessionCreateBody = Extract<
  ImpersonationSessionCreate,
  { targetAccountId: string }
>;

/**
 * Hand-written client for `POST /v3/platform/impersonation-sessions`.
 *
 * WHY THIS ISN'T IN THE SDK: every operation on the spec's `Impersonation` tag
 * carries `x-internal: true`, and OpenAPI Generator skips `x-internal`
 * operations — so no `ImpersonationApi` class is emitted at all (same for
 * `Health`, and for the internal-only operations on `Notifications` and
 * `Mandates`). The `ImpersonationSession*` **models** still generate, because
 * they live in `components/schemas`, which is why the types here come from the
 * SDK while the call itself is written by hand. Staff-internal operations are
 * deliberately kept out of the published client, so this is the call's
 * long-term home rather than a stopgap awaiting an SDK release.
 *
 * It extends the SDK's `BaseAPI` so the request still goes through the shared
 * `apiConfig` middleware chain — `authRefreshMiddleware`,
 * `mfaChallengeMiddleware` (this endpoint is `x-wingspan-auth-mode: elevated`
 * and `FullAdmin` requires recent MFA) and `typedErrorMiddleware`, so failures
 * surface as the same typed SDK errors every other v3 call throws.
 */
class ImpersonationApi extends BaseAPI {
  async createImpersonationSession(requestParameters: {
    impersonationSessionCreate: ImpersonationSessionCreateBody;
  }): Promise<ImpersonationSessionWithToken> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    // `BaseAPI` does not apply security schemes — every generated method
    // resolves `bearerAuth` itself, so this mirrors that exactly.
    const { accessToken } = this.configuration;
    if (accessToken) {
      const token = await accessToken("bearerAuth", []);
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const response = await this.request({
      path: "/v3/platform/impersonation-sessions",
      method: "POST",
      headers,
      body: requestParameters.impersonationSessionCreate
    });

    return new JSONApiResponse(response, jsonValue =>
      ImpersonationSessionWithTokenFromJSON(jsonValue)
    ).value();
  }
}

export const impersonationApi = new ImpersonationApi(apiConfig);
